import {
  ICreateCommercialPlanInputModel,
  IUpdateCommercialPlanInputModel,
  IAddFontRuleInputModel,
  IUpdateFontRuleInputModel,
  IOrderedWithPaginationInputModel,
} from 'inputModels';
import api from 'services/api';
import {
  ICommercialPlanViewModel,
  IFontRuleViewModel,
  IResponse,
  IVoidResponse,
  IUserViewModel,
} from 'viewModels';

const getAll = (input: IOrderedWithPaginationInputModel<string>) =>
  new Promise<IResponse<ICommercialPlanViewModel[]>>((resolve, reject) => {
    api
      .get('commercialplan', { params: input })
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

const getById = (id: string) =>
  new Promise<IResponse<ICommercialPlanViewModel>>((resolve, reject) => {
    api
      .get(`commercialplan/${id}`)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

const create = (input: ICreateCommercialPlanInputModel) =>
  new Promise<IResponse<ICommercialPlanViewModel>>((resolve, reject) => {
    api
      .post('commercialplan', input)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

const update = (id: string, input: IUpdateCommercialPlanInputModel) =>
  new Promise<IResponse<ICommercialPlanViewModel>>((resolve, reject) => {
    api
      .put(`commercialplan/${id}`, input)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

const remove = (id: string) =>
  new Promise<IVoidResponse>((resolve, reject) => {
    api
      .delete(`commercialplan/${id}`)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

const addUserToPlan = (planId: string, userId: string) =>
  new Promise<IVoidResponse>((resolve, reject) => {
    api
      .post(`commercialplan/${planId}/users/${userId}`)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

const removeUserFromPlan = (planId: string, userId: string) =>
  new Promise<IVoidResponse>((resolve, reject) => {
    api
      .delete(`commercialplan/${planId}/users/${userId}`)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

const getPlanUsers = (
  planId: string,
  input: IOrderedWithPaginationInputModel<string>
) =>
  new Promise<IResponse<IUserViewModel[]>>((resolve, reject) => {
    api
      .get(`commercialplan/${planId}/users`, { params: input })
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

const addFontRule = (planId: string, input: IAddFontRuleInputModel) =>
  new Promise<IVoidResponse>((resolve, reject) => {
    api
      .post(`commercialplan/${planId}/font-rules`, input)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

const updateFontRule = (
  planId: string,
  fontKey: string,
  input: IUpdateFontRuleInputModel
) =>
  new Promise<IVoidResponse>((resolve, reject) => {
    api
      .put(`commercialplan/${planId}/font-rules/${fontKey}`, input)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

const removeFontRule = (planId: string, fontKey: string) =>
  new Promise<IVoidResponse>((resolve, reject) => {
    api
      .delete(`commercialplan/${planId}/font-rules/${fontKey}`)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

const getPlanFontRules = (planId: string) =>
  new Promise<IResponse<IFontRuleViewModel[]>>((resolve, reject) => {
    api
      .get(`commercialplan/${planId}/font-rules`)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

const setAsDefault = (id: string) =>
  new Promise<IVoidResponse>((resolve, reject) => {
    api
      .post(`commercialplan/${id}/set-as-default`)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

const activate = (id: string) =>
  new Promise<IVoidResponse>((resolve, reject) => {
    api
      .post(`commercialplan/${id}/activate`)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

const deactivate = (id: string) =>
  new Promise<IVoidResponse>((resolve, reject) => {
    api
      .post(`commercialplan/${id}/deactivate`)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

const getDefault = () =>
  new Promise<IResponse<ICommercialPlanViewModel>>((resolve, reject) => {
    api
      .get('commercialplan/default')
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

const getUserCommercialPlan = (userId: string) =>
  new Promise<IResponse<ICommercialPlanViewModel>>((resolve, reject) => {
    api
      .get(`commercialplan/${userId}/get-user-commercial-plan`)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

const transferUsers = (sourcePlanId: string, targetPlanId: string, userIds: string[]) =>

  new Promise<IVoidResponse>((resolve, reject) => {
    api
      .post(`commercialplan/${sourcePlanId}/transfer-users/${targetPlanId}`, userIds)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

export const CommercialPlanController = {
  getAll,
  getById,
  create,
  update,
  remove,
  addUserToPlan,
  removeUserFromPlan,
  getPlanUsers,
  addFontRule,
  updateFontRule,
  removeFontRule,
  getPlanFontRules,
  setAsDefault,
  activate,
  deactivate,
  getDefault,
  getUserCommercialPlan,
  transferUsers,
};
